import { IcoAlertTriangle, IcoCheck } from "../icons";

export type AssignmentMember = {
  id: string;
  userName: string;
  userEmail: string;
};

type AssignmentCardProps = {
  assignedMemberIds: string[];
  members: AssignmentMember[];
  onClick: () => void;
};

export function AssignmentCard({
  assignedMemberIds,
  members,
  onClick,
}: AssignmentCardProps) {
  const assignedCount = assignedMemberIds.length;
  const totalCount = members.length;
  const assignedMembers = members.filter((m) =>
    assignedMemberIds.includes(m.id),
  );

  // Empty state - warning
  if (assignedCount === 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        class="w-full text-left rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 p-4 hover:border-amber-400 dark:hover:border-amber-500 transition-colors group"
      >
        <div class="flex items-start gap-3">
          <div class="size-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/50 flex-shrink-0">
            <IcoAlertTriangle class="size-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-amber-800 dark:text-amber-200">
              No one will receive this form
            </p>
            <p class="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
              Assign people to start sending check-ins
            </p>
          </div>
          <span class="px-3 py-1.5 rounded-lg bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-100 text-sm font-medium group-hover:bg-amber-300 dark:group-hover:bg-amber-700 transition-colors flex-shrink-0">
            Assign
          </span>
        </div>
      </button>
    );
  }

  // Assigned state - show who's assigned
  const displayMembers = assignedMembers.slice(0, 3);
  const remainingCount = assignedCount - displayMembers.length;

  return (
    <button
      type="button"
      onClick={onClick}
      class="w-full text-left rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-4 hover:border-green-300 dark:hover:border-green-700 transition-colors group"
    >
      <div class="flex items-start gap-3">
        <div class="size-10 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/50 flex-shrink-0">
          <IcoCheck class="size-5 text-green-600 dark:text-green-400" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-green-800 dark:text-green-200">
            Assigned to {assignedCount} of {totalCount} member
            {totalCount !== 1 ? "s" : ""}
          </p>
          <div class="flex items-center gap-2 mt-2 flex-wrap">
            {displayMembers.map((member) => (
              <span
                key={member.id}
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 text-xs font-medium"
              >
                <MemberAvatar name={member.userName || member.userEmail} />
                {getFirstName(member.userName) || member.userEmail}
              </span>
            ))}
            {remainingCount > 0 && (
              <span class="inline-flex items-center px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs font-medium">
                +{remainingCount} more
              </span>
            )}
          </div>
        </div>
        <span class="px-3 py-1.5 rounded-lg bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100 text-sm font-medium group-hover:bg-green-300 dark:group-hover:bg-green-700 transition-colors flex-shrink-0">
          Manage
        </span>
      </div>
    </button>
  );
}

function MemberAvatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <span class="size-5 rounded-full bg-green-200 dark:bg-green-800 flex items-center justify-center text-[10px] font-bold text-green-700 dark:text-green-200">
      {initials || "?"}
    </span>
  );
}

function getFirstName(name: string): string {
  return name?.split(" ")[0] || "";
}
